import React from 'react';
import { IconArrowLeft, IconPalette, IconCheck, IconSun, IconMoon, IconDroplet } from '@tabler/icons-react';
import { useTheme, Theme } from '../contexts/ThemeContext';
interface AppearancePageProps {
  onBack: () => void;
}
interface ThemeOption {
  id: Theme;
  name: string;
  description: string;
  icon: React.ElementType;
  preview: {
    background: string;
    surface: string;
    text: string;
    accent: string;
  };
}
export function AppearancePage({
  onBack
}: AppearancePageProps) {
  const {
    theme: selectedTheme,
    setTheme
  } = useTheme();
  const themes: ThemeOption[] = [{
    id: 'light',
    name: 'Chiaro',
    description: 'Tema chiaro con sfondo luminoso',
    icon: IconSun,
    preview: {
      background: '#F5F5F5',
      surface: '#FFFFFF',
      text: '#1D2A36',
      accent: '#B0EC9C'
    }
  }, {
    id: 'dark',
    name: 'Scuro',
    description: "Tema scuro per ridurre l'affaticamento visivo",
    icon: IconMoon,
    preview: {
      background: '#1D2A36',
      surface: '#2A3A4A',
      text: '#F5F5F5',
      accent: '#B0EC9C'
    }
  }, {
    id: 'navy',
    name: 'Navy',
    description: 'Tema blu navy elegante',
    icon: IconDroplet,
    preview: {
      background: '#0F1B2E',
      surface: '#1A2B42',
      text: '#F5F5F5',
      accent: '#AEDAFF'
    }
  }];
  const handleThemeSelect = (themeId: Theme) => {
    setTheme(themeId);
  };
  return <div className="flex flex-col h-full" data-id="element-536">
      {/* Header */}
      <div className="px-4 pt-4 pb-3" data-id="element-537">
        <div className="flex items-center gap-3 mb-3" data-id="element-538">
          <button onClick={onBack} className="flex items-center gap-1 py-2 pr-2 transition-opacity hover:opacity-70" aria-label="Torna a Impostazioni" data-id="element-539">
            <IconArrowLeft className="w-5 h-5 text-yeppy-text" stroke={2} data-id="element-540" />
            <span className="font-poppins text-base text-yeppy-text font-medium" data-id="element-541">
              Impostazioni
            </span>
          </button>
          <div className="flex-1 flex justify-end" data-id="element-542">
            <h1 className="font-rubik font-bold text-lg text-yeppy-text" data-id="element-543">
              Aspetto
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6" data-id="element-544">
        <div className="flex items-center justify-center py-6" data-id="element-545">
          <div className="w-20 h-20 rounded-full bg-yeppy-blue bg-opacity-20 flex items-center justify-center" data-id="element-546">
            <IconPalette className="w-10 h-10 text-yeppy-blue" stroke={1.5} data-id="element-547" />
          </div>
        </div>
        <div className="max-w-md mx-auto space-y-6" data-id="element-548">
          {/* Theme Selection Section */}
          <div className="bg-white border border-yeppy-text border-opacity-10 rounded-2xl p-5" data-id="element-549">
            <div className="flex items-center gap-3 mb-4" data-id="element-550">
              <div className="w-10 h-10 rounded-xl bg-yeppy-bg flex items-center justify-center flex-shrink-0" data-id="element-551">
                <IconPalette className="w-5 h-5 text-yeppy-text" stroke={1.5} data-id="element-552" />
              </div>
              <span className="font-poppins text-sm font-medium text-yeppy-text flex-1" data-id="element-553">
                Tema
              </span>
            </div>

            <p className="font-poppins text-sm text-yeppy-text text-opacity-60 mb-4" data-id="element-554">
              Scegli il tema che preferisci per l'interfaccia
            </p>

            <div className="space-y-3" data-id="element-555">
              {themes.map(theme => {
              const isSelected = selectedTheme === theme.id;
              const ThemeIcon = theme.icon;
              return <button key={theme.id} onClick={() => handleThemeSelect(theme.id)} className={`w-full rounded-2xl p-4 transition-all text-left ${isSelected ? 'bg-white border-2 border-yeppy-blue' : 'bg-white border border-yeppy-text border-opacity-10'}`} data-id="element-556">
                    <div className="flex items-start gap-3" data-id="element-557">
                      {/* Theme Preview */}
                      <div className="flex-shrink-0" data-id="element-558">
                        <div className="w-16 h-16 rounded-xl border border-yeppy-text border-opacity-10 overflow-hidden" style={{
                      backgroundColor: theme.preview.background
                    }} data-id="element-559">
                          <div className="h-full flex flex-col" data-id="element-560">
                            {/* Preview top bar */}
                            <div className="h-3" style={{
                          backgroundColor: theme.preview.surface
                        }} data-id="element-561" />
                            {/* Preview content area */}
                            <div className="flex-1 p-1.5 space-y-1" data-id="element-562">
                              <div className="h-1.5 rounded-full" style={{
                            backgroundColor: theme.preview.text,
                            opacity: 0.6,
                            width: '80%'
                          }} data-id="element-563" />
                              <div className="h-1.5 rounded-full" style={{
                            backgroundColor: theme.preview.text,
                            opacity: 0.4,
                            width: '60%'
                          }} data-id="element-564" />
                              <div className="h-2 w-8 rounded-full mt-1" style={{
                            backgroundColor: theme.preview.accent
                          }} data-id="element-565" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Theme Info */}
                      <div className="flex-1 min-w-0" data-id="element-566">
                        <div className="flex items-center gap-2 mb-1" data-id="element-567">
                          <ThemeIcon className="w-4 h-4 text-yeppy-text" stroke={1.5} data-id="element-568" />
                          <h3 className="font-rubik font-semibold text-base text-yeppy-text" data-id="element-569">
                            {theme.name}
                          </h3>
                        </div>
                        <p className="font-poppins text-sm text-yeppy-text text-opacity-60" data-id="element-570">
                          {theme.description}
                        </p>
                      </div>

                      {/* Selected Indicator */}
                      {isSelected && <div className="flex-shrink-0" data-id="element-571">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{
                      backgroundColor: '#AEDAFF'
                    }} data-id="element-572">
                            <IconCheck className="w-4 h-4" stroke={2.5} style={{
                        color: '#1D2A36'
                      }} data-id="element-573" />
                          </div>
                        </div>}
                    </div>
                  </button>;
            })}
            </div>
          </div>

          {/* Info Note */}
          <div className="bg-yeppy-yellow bg-opacity-20 border border-yeppy-yellow border-opacity-30 rounded-2xl p-4" data-id="element-574">
            <p className="font-poppins text-sm text-yeppy-text text-opacity-80" data-id="element-575">
              <span className="font-semibold" data-id="element-576">Nota:</span> Il tema selezionato
              verrà applicato immediatamente a tutte le schermate dell'app.
            </p>
          </div>
        </div>
      </div>
    </div>;
}